import {
  clearAuthSession,
  ClientAuthSession,
  getActiveSession,
  getBootstrapTenantId,
  getRefreshToken,
  isSessionExpired,
  persistAuthTokensFromPayload,
  setActiveSession,
} from './authSession';
import { pathTenantHeaders } from '../config/workspaceHost';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

export interface RefreshSessionResult extends ClientAuthSession {}

type TenantRequestContext = {
  tenantId?: string;
  tenantUrl?: string;
};

type AuthSessionPayload = {
  username?: string;
  tenantUrl?: string;
  expiresAt?: string;
  accessToken?: string;
  refreshToken?: string;
  allowedTenantIds?: string[];
  employeeIdsByTenant?: Record<string, string>;
  permissions?: string[];
  isPlatformOperator?: boolean;
};

const buildRefreshRequestBody = (): string => {
  const refreshToken = getRefreshToken();
  return JSON.stringify(refreshToken ? { refreshToken } : {});
};

const resolveTenantHeaders = (tenant?: TenantRequestContext): Record<string, string> => {
  const tenantId = (tenant?.tenantId || getBootstrapTenantId() || '').trim();
  const tenantUrl = (tenant?.tenantUrl || (typeof window !== 'undefined' ? window.location.origin : '') || '').trim();

  return {
    'Content-Type': 'application/json',
    ...(tenantId ? { TenantId: tenantId, 'X-Tenant-Id': tenantId } : {}),
    ...(tenantUrl ? { 'X-Tenant-Url': tenantUrl } : {}),
    ...pathTenantHeaders(),
  };
};

const normalizePermissions = (permissions: unknown): string[] => {
  if (!Array.isArray(permissions)) {
    return [];
  }

  return Array.from(new Set(
    permissions
      .filter((value): value is string => typeof value === 'string')
      .map(value => value.trim().toLowerCase())
      .filter(Boolean),
  )).sort();
};

const resolveTenantId = (payload: AuthSessionPayload, preferredTenantId?: string): string | null => {
  const allowed = Array.isArray(payload.allowedTenantIds)
    ? payload.allowedTenantIds.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    : [];

  if (!allowed.length) {
    return preferredTenantId?.trim() || null;
  }

  const preferred = (preferredTenantId || getBootstrapTenantId() || '').trim();
  if (!preferred) {
    return allowed[0];
  }

  const direct = allowed.find(tenant => tenant.toLowerCase() === preferred.toLowerCase());
  return direct || allowed[0];
};

const resolveEmployeeId = (payload: AuthSessionPayload, tenantId: string): string | null => {
  const map = payload.employeeIdsByTenant;
  if (!map || typeof map !== 'object') {
    return null;
  }

  const direct = map[tenantId] ?? map[tenantId.toLowerCase()] ?? map[tenantId.toUpperCase()];
  if (typeof direct !== 'string' || !direct.trim()) {
    return null;
  }

  return direct.trim();
};

const toClientSession = (payload: AuthSessionPayload, preferredTenantId?: string): ClientAuthSession => {
  const tenantId = resolveTenantId(payload, preferredTenantId);
  if (!tenantId) {
    throw new Error('Session response returned no accessible tenant.');
  }

  const username = typeof payload.username === 'string' && payload.username.trim()
    ? payload.username.trim()
    : getActiveSession()?.username || '';

  const expiresAt = typeof payload.expiresAt === 'string' && payload.expiresAt.trim()
    ? payload.expiresAt
    : new Date(Date.now() + 55 * 60 * 1000).toISOString();

  return {
    username,
    tenantId,
    employeeId: resolveEmployeeId(payload, tenantId),
    permissions: normalizePermissions(payload.permissions),
    expiresAt,
    isPlatformOperator: payload.isPlatformOperator === true,
  };
};

let refreshInFlight: Promise<RefreshSessionResult | null> | null = null;

export const refreshAuthSession = async (
  tenant?: TenantRequestContext,
): Promise<RefreshSessionResult | null> => {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: resolveTenantHeaders(tenant),
      body: buildRefreshRequestBody(),
    });

    let payload: AuthSessionPayload | null = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      throw new Error('Session refresh failed.');
    }

    persistAuthTokensFromPayload(payload);
    const session = toClientSession(payload || {}, tenant?.tenantId);
    setActiveSession(session);
    return session;
  })()
    .catch(error => {
      clearAuthSession();
      throw error;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
};

export const ensureFreshSession = async (): Promise<ClientAuthSession | null> => {
  const currentSession = getActiveSession();
  if (currentSession && !isSessionExpired(currentSession)) {
    return currentSession;
  }

  try {
    return await refreshAuthSession({
      tenantId: currentSession?.tenantId,
    });
  } catch {
    return null;
  }
};

export const AUTH_SESSION_REFRESHED_EVENT = 'auth:session-refreshed';

export const notifyAuthSessionRefreshed = (session: RefreshSessionResult): void => {
  window.dispatchEvent(new CustomEvent(AUTH_SESSION_REFRESHED_EVENT, { detail: session }));
};

export const logoutAuthSession = async (): Promise<void> => {
  try {
    await fetch(`${API_BASE_URL}/api/v1/auth/logout`, {
      method: 'POST',
      headers: resolveTenantHeaders(),
    });
  } catch {
    // Best effort — local session is cleared regardless.
  } finally {
    clearAuthSession();
  }
};

export const parseAuthSessionPayload = (
  payload: unknown,
  preferredTenantId?: string,
): ClientAuthSession => {
  persistAuthTokensFromPayload(payload);
  return toClientSession((payload || {}) as AuthSessionPayload, preferredTenantId);
};
